import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { getProductImage } from '../../utils/productImages';
import './FestivalPassModal.css';

/**
 * Upsell festival-pass modal.
 *
 * Purchase button behavior (as of Apr 2026):
 *   Instead of off-session auto-charging the buyer's saved card, we prefill
 *   the main `/cart` with the festival pass at its normal price and auto-
 *   apply a *per-buyer, single-use* coupon minted by the backend. The
 *   checkout page then handles:
 *     - saved-card quick purchase (if one is on file), OR
 *     - Stripe Checkout redirect for a fresh card.
 *
 *   This means the buyer ALWAYS sees the amount on a confirmation screen
 *   before they get charged, which is the behavior we want post-400-bug.
 *
 * Props:
 *   sessionId         The original Stripe session id (string).
 *   passSlug          '1day' | '2day'.
 *   passName          e.g. "2-Day Pass".
 *   priceCents        Discounted per-ticket price (e.g. 1500 for $15).
 *   originalCents     Original (strikethrough) per-ticket price (e.g. 3000).
 *   defaultQuantity   Quantity pre-selected on open (matches tickets bought).
 *   passTicketTypeId  Real festival TicketType UUID (from offer config).
 *   passFullPriceCents  Full pre-discount price in cents (for the cart line).
 *   passCouponCode    Per-buyer upsell coupon code (from offer config).
 *   buyerEmail        Normalized buyer email stamped into LS for Cart
 *                     cross-user-leak guard.
 *   remainingSeconds  Seconds left in the 20-min upsell window — used to
 *                     compute the LS `expiresAt` so Cart auto-clears.
 *   onClose           () => void
 *   onPurchased       (resultData) => void — called after we redirect to
 *                     /cart so the banner can mark itself dismissed.
 */

const dollars = (cents) =>
  `$${(Math.max(0, Number(cents) || 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const MAX_QTY = 20;

const FestivalPassModal = ({
  sessionId,
  passSlug,
  passName,
  priceCents,
  originalCents,
  defaultQuantity = 1,
  passTicketTypeId = null,
  passFullPriceCents = null,
  passCouponCode = null,
  buyerEmail = null,
  remainingSeconds = 0,
  onClose,
  onPurchased,
}) => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(() => Math.max(1, Math.min(defaultQuantity || 1, MAX_QTY)));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const close = useCallback(() => {
    if (submitting) return;
    if (typeof onClose === 'function') onClose();
  }, [submitting, onClose]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const totalCents = priceCents * quantity;
  const totalOriginalCents = originalCents * quantity;

  // --- Purchase button ------------------------------------------------------
  // NO auto-charge here. The button:
  //   1. Writes the pass as a cart item into `localStorage.cart` at the
  //      FULL price (backend will discount via the per-buyer coupon).
  //   2. Writes `upsellWindow` + `upsellPassCoupon` to LS (stamped with
  //      buyerEmail so a different user on the same browser doesn't
  //      inherit the discount — same pattern as the AMP flow).
  //   3. Fires the `clicked_purchase_pass` analytics event. The backend
  //      will mark the origin order 'accepted' when the follow-up order
  //      finalizes (via `upsell_source_session_id` in checkout metadata).
  //   4. Navigates to `/cart` where the buyer confirms (and the Cart
  //      quick-purchases via saved card, or redirects to Stripe).
  const handlePurchase = async () => {
    if (submitting) return;
    // Guard: we need at least a ticket_type_id to build a valid cart entry.
    if (!passTicketTypeId) {
      setError('This offer is missing product information. Please refresh and try again.');
      return;
    }
    setSubmitting(true);
    setError('');

    const normalizedEmail = (buyerEmail || '').trim().toLowerCase() || null;
    const expiresAt = Date.now() + Math.max(60, remainingSeconds || 0) * 1000;
    // Full (undiscounted) price per ticket in dollars — Cart items store
    // price in dollars. The coupon will apply the discount server-side.
    const unitPriceDollars = (Number(passFullPriceCents || originalCents || 0) || 0) / 100;

    try {
      // 1. Merge the pass into the cart. We REPLACE any existing festival
      //    items to avoid doubling up on the same weekend, but preserve
      //    AMP and other items (matches FestivalTickets.jsx merge logic).
      let existingItems = [];
      try {
        const raw = localStorage.getItem('cart');
        if (raw) existingItems = (JSON.parse(raw).items || []);
      } catch { /* ignore */ }
      const nonFestival = existingItems.filter((i) => i.type !== 'festival');
      const newItem = {
        id: passTicketTypeId,
        ticket_type_id: passTicketTypeId,
        name: passName,
        quantity,
        price: unitPriceDollars,
        type: 'festival',
      };
      const mergedItems = [...nonFestival, newItem];
      const newTotal = mergedItems.reduce(
        (sum, i) => sum + Number(i.price || 0) * Number(i.quantity || 1),
        0,
      );
      localStorage.setItem('cart', JSON.stringify({ items: mergedItems, total: newTotal }));

      // 2. Stash the upsell coupon + window so Cart auto-applies and
      //    hides the manual coupon input. Stamped with buyerEmail to
      //    defeat cross-user leakage.
      if (passCouponCode) {
        localStorage.setItem('upsellPassCoupon', JSON.stringify({
          sessionId,
          code: passCouponCode,
          passSlug,
          quantity,
          expiresAt,
          buyerEmail: normalizedEmail,
        }));
      }
      localStorage.setItem('upsellWindow', JSON.stringify({
        sessionId,
        caseKey: null,
        expiresAt,
        buyerEmail: normalizedEmail,
      }));

      // 3. Analytics: the buyer clicked Purchase on the modal. The upsell
      //    offer stays 'active' on the backend until the follow-up
      //    checkout completes (which then marks it 'accepted' via
      //    the existing `checkout_redirect` event path).
      api.trackUpsellAction(sessionId, 'clicked_purchase_pass', {
        pass_slug: passSlug,
        quantity,
      }).catch(() => {});

      // 4. Tell parent we're redirecting, then navigate.
      if (typeof onPurchased === 'function') {
        onPurchased({ redirecting: true, pass_slug: passSlug, quantity });
      }
      // Also signal global cart update so header badge refreshes.
      try {
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { items: mergedItems, total: newTotal },
        }));
      } catch { /* ignore */ }
      navigate('/cart');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Upsell modal redirect failed:', err);
      setError('Could not open checkout. Please try again.');
      setSubmitting(false);
    }
  };

  const inc = () => setQuantity((q) => Math.min(MAX_QTY, q + 1));
  const dec = () => setQuantity((q) => Math.max(1, q - 1));

  const imageSrc = getProductImage({ name: passName });

  return (
    <div className="upm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="upm-dialog" role="dialog" aria-modal="true" aria-labelledby="upm-title">
        <div className="upm-dialog__header">
          <h3 id="upm-title" className="upm-dialog__title">Make it a full 3-day weekend!</h3>
        </div>

        <div className="upm-dialog__body">
          <div className="upm-pass">
            <img className="upm-pass__img" src={imageSrc} alt={passName} />
            <div className="upm-pass__details">
              <div className="upm-qty">
                <span className="upm-qty__label">How many tickets?</span>
                <div className="upm-qty__stepper" role="group" aria-label="Quantity">
                  <button
                    type="button"
                    className="upm-qty__btn"
                    onClick={dec}
                    disabled={submitting || quantity <= 1}
                    aria-label="Decrease quantity"
                  >−</button>
                  <span className="upm-qty__value" aria-live="polite">{quantity}</span>
                  <button
                    type="button"
                    className="upm-qty__btn"
                    onClick={inc}
                    disabled={submitting || quantity >= MAX_QTY}
                    aria-label="Increase quantity"
                  >+</button>
                </div>
              </div>
              <div className="upm-pass__prices" aria-live="polite">
                <span className="upm-pass__orig">{dollars(totalOriginalCents)}</span>
                <span className="upm-pass__final">{dollars(totalCents)}</span>
              </div>
            </div>
          </div>

          {error && <div className="upm-error" role="alert">{error}</div>}
        </div>

        <div className="upm-dialog__footer">
          <button
            type="button"
            className="upm-btn upm-btn--cancel"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="upm-btn upm-btn--purchase"
            onClick={handlePurchase}
            disabled={submitting}
          >
            {submitting ? 'Opening checkout…' : `Continue to Checkout (${dollars(totalCents)})`}
          </button>
        </div>

        <div className="upm-footnotes">
          <p className="upm-footnotes__lead">*Cannot be combined with other offers or coupons.</p>
          <p>Pacific Amphitheater tickets include same-day festival admission.</p>
          <p>Festival Tickets do not include access to Pacific Amphitheater performances.</p>
        </div>
      </div>
    </div>
  );
};

export default FestivalPassModal;
